// // Allow us to cache the realm app client
let app;

// IndexedDB helper functions
const DB_NAME = 'BGMarketingFitments';
const DB_VERSION = 1;
const STORE_NAME = 'preferences';

// Variant id for the tyre disposal fee product that gets auto-managed in the cart
const TYRE_FEE_VARIANT_ID = 49811589005617;

// Track the state of automatic tyre fee synchronisation to avoid infinite retry loops
const tyreFeeSyncState = {
  inProgress: false,
  desiredQty: null,
  attempts: 0,
  failureLogged: false,
};

// Initialize IndexedDB
function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('customerId', 'customerId', { unique: false });
      }
    };
  });
}

// Get value from IndexedDB
async function getFromDB(key) {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result;
        // If we have a result object with a value property, return the value
        // Otherwise return null (matching Vue app behavior)
        resolve(result ? result.value : null);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting from IndexedDB:', error);
    // Fallback to localStorage for backwards compatibility
    return localStorage.getItem(key);
  }
}

// Set value in IndexedDB
async function setInDB(key, value) {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    // Create an object matching Vue app's storage structure
    // Note: We intentionally don't set customerId for pricesHidden
    // as it should be browser-specific, not customer-specific
    const item = {
      key: key,
      value: value,
      timestamp: new Date()
    };
    
    store.put(item);
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error setting in IndexedDB:', error);
    // Fallback to localStorage for backwards compatibility
    localStorage.setItem(key, value);
  }
}

// Remove value from IndexedDB
async function removeFromDB(key) {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    store.delete(key);
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error removing from IndexedDB:', error);
  }
  
  // Also remove from localStorage and sessionStorage for backwards compatibility
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

// Clear PIN verification when logging out
async function clearPinVerification() {
  await removeFromDB('pinVerification');
}

// Wait for Realm to be available on window
const waitForRealm = () => {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds max wait
    
    const checkRealm = () => {
      attempts++;
      
      if (typeof window !== 'undefined' && window.Realm) {
        resolve(true);
      } else if (attempts >= maxAttempts) {
        reject(new Error('Realm SDK failed to load after 5 seconds'));
      } else {
        setTimeout(checkRealm, 100);
      }
    };
    
    checkRealm();
  });
};

// Function to set the visibility flag in IndexedDB
async function setPricesVisibility(action) {
  try {
    if (action === "hide") {
      await setInDB("pricesHidden", true);
    } else if (action === "show") {
      await setInDB("pricesHidden", false);
    }

    // Dispatch custom event for Vue components
    window.dispatchEvent(new CustomEvent("pricesVisibilityChanged"));

    // Call function to update the visibility based on the new state
    updatePricesVisibility();

    // Log for debugging
    console.log(`Prices visibility set to: ${action}`);
  } catch (error) {
    console.error("Error setting prices visibility:", error);
  }
}

// Function to update the visibility of prices and buttons based on IndexedDB
async function updatePricesVisibility() {
  try {
    const pricesHiddenValue = await getFromDB("pricesHidden");
    const pricesHidden = pricesHiddenValue === true || pricesHiddenValue === "true";

    // Product grid prices
    const prices = document.querySelectorAll(".rapido-item-price");
    const priceColumns = document.querySelectorAll(".price-column");

    // Cart-related prices
    const linePrices = document.querySelectorAll(".rapido-line-price");
    const subtotals = document.querySelectorAll(".rapido-subtotal");
    const freightAmounts = document.querySelectorAll(".rapido-freight-amount");
    const taxAmounts = document.querySelectorAll(".rapido-taxamount");
    const grossTotals = document.querySelectorAll(".rapido-grosstotal");

    const hideButton = document.getElementById("hideButton");
    const showButton = document.getElementById("showButton");

    // Update legacy DOM elements if they exist
    if (hideButton && showButton) {
      if (pricesHidden) {
        // Hide product grid prices
        prices.forEach((price) => {
          price.style.display = "none";
        });
        priceColumns.forEach((column) => {
          column.style.display = "none";
        });

        // Hide cart prices
        linePrices.forEach((price) => {
          price.style.display = "none";
        });
        subtotals.forEach((subtotal) => {
          subtotal.style.display = "none";
        });
        freightAmounts.forEach((freight) => {
          freight.style.display = "none";
        });
        taxAmounts.forEach((tax) => {
          tax.style.display = "none";
        });
        grossTotals.forEach((total) => {
          total.style.display = "none";
        });

        hideButton.style.display = "none";
        showButton.style.display = "block";
      } else {
        // Show product grid prices
        prices.forEach((price) => {
          price.style.display = "block";
        });
        priceColumns.forEach((column) => {
          column.style.display = "table-cell";
        });

        // Show cart prices
        linePrices.forEach((price) => {
          price.style.display = "block";
        });
        subtotals.forEach((subtotal) => {
          subtotal.style.display = "block";
        });
        freightAmounts.forEach((freight) => {
          freight.style.display = "block";
        });
        taxAmounts.forEach((tax) => {
          tax.style.display = "block";
        });
        grossTotals.forEach((total) => {
          total.style.display = "block";
        });

        hideButton.style.display = "block";
        showButton.style.display = "none";
      }
    }

    console.log(
      `Prices visibility updated: ${pricesHidden ? "hidden" : "visible"}`
    );
  } catch (error) {
    console.error("Error updating prices visibility:", error);
  }
}

function showCartLoggedIn() {
  const loggedInStatus = document.getElementById("rapido-status").value;
  if (loggedInStatus === "logged_in") {
    const cartElem = document.getElementById("HeaderCart");
    if (cartElem) {
      // Check if the element exists
      cartElem.style.display = "inline-flex"; // Hide the element
    }
  }
}
document.addEventListener("DOMContentLoaded", function () {
  showCartLoggedIn(); // Call your function when the document is ready
  updatePricesVisibility();
  
  // Clear pin verification if user is logged out
  const statusElement = document.getElementById("rapido-status");
  if (statusElement && statusElement.value === "logged_out") {
    clearPinVerification().catch(err => 
      console.error("Error clearing pin verification on logout:", err)
    );
  }
});

// Listen for price visibility changes from Vue components
window.addEventListener("pricesVisibilityChanged", function () {
  updatePricesVisibility();
});

async function rapido_getRealmUser() {
  // Ensure Realm is available before using it
  await waitForRealm();
  
  const realmAppClient = "bgm-shopify-iwqes";
  let credential;
  const shopify_hmac = document.getElementById("rapido-identify").value;
  const customer_id = parseInt(document.getElementById("rapido-uniq").value);
  const combined = document.getElementById("rapido-t").value;
  if (!app) {
    app = new window.Realm.App({
      id: realmAppClient,
      baseUrl: "https://api.bgworldwheels.co.nz",
      skipLocationRequest: true,
    });

    credential = window.Realm.Credentials.function({
      shopify_hmac,
      customer_id,
      combined,
    });
  }

  const loggedInStatus = document.getElementById("rapido-status").value;
  if (!app.currentUser) {
    await app.logIn(credential);
    return app.currentUser;
  } else {
    if (loggedInStatus === "logged_in" && app.currentUser.id === "-1") {
      // Log out and log back in
      await app.currentUser.logOut();
      localStorage.clear();
      await clearPinVerification();
      credential = window.Realm.Credentials.function({
        shopify_hmac,
        customer_id,
        combined,
      });
      await app.logIn(credential);
      return app.currentUser;
    } else if (loggedInStatus === "logged_out" && app.currentUser.id !== "-1") {
      // Log out and log back in
      await app.currentUser.logOut();
      localStorage.clear();
      await clearPinVerification();
      await app.logIn(credential);
      return app.currentUser;
    }
    return app.currentUser;
  }
}

// Helper function to handle 401 errors and retry with fresh authentication
async function executeRealmFunction(getUserAndCall, retryCount = 0) {
  try {
    return await getUserAndCall();
  } catch (error) {
    // Check if it's a 401 Unauthorized error
    if (error?.statusCode === 401 || error?.error === 'Unauthorized' || error?.message?.includes('401')) {
      // Prevent infinite loop by limiting retries
      if (retryCount >= 1) {
        console.error('Re-authentication failed after retry, giving up');
        throw new Error('Authentication failed: Unable to re-authenticate after 401 error');
      }
      
      console.log('Got 401 error, clearing local storage and re-authenticating...');
      
      try {
        // Clear local storage 
        localStorage.clear();
        
        // Force re-authentication
        if (app && app.currentUser) {
          await app.currentUser.logOut();
        }
        app = null;
        
        // Get credentials from page again
        const shopify_hmac = document.getElementById('rapido-identify').value;
        const customer_id = parseInt(document.getElementById('rapido-uniq').value);
        const combined = document.getElementById('rapido-t').value;
        
        // Re-initialize app and login
        app = new window.Realm.App({
          id: 'bgm-shopify-iwqes',
          baseUrl: 'https://api.bgworldwheels.co.nz',
          skipLocationRequest: true
        });
        
        const credential = window.Realm.Credentials.function({
          shopify_hmac,
          customer_id,
          combined
        });
        
        await app.logIn(credential);
        
        // Retry the original function call (which will get the new user)
        return await executeRealmFunction(getUserAndCall, retryCount + 1);
      } catch (reAuthError) {
        console.error('Re-authentication failed:', reAuthError);
        throw new Error('Authentication failed: Unable to re-authenticate - ' + reAuthError.message);
      }
    }
    
    // Re-throw if it's not a 401 error
    throw error;
  }
}

async function rapido_updatePrices() {
  const user = await rapido_getRealmUser();
  if (user.id === "-1") {
    // Public user, no prices
    return;
  }
  const itemPrices = document.getElementsByClassName("rapido-item-price");
  const itemPricesArray = [...itemPrices];
  const pricesPayload = itemPricesArray.map((itemPrice) => {
    const { dataset } = itemPrice;
    const { sku } = dataset;
    return { ProductCode: sku, Quantity: 1 };
  });

  const itemPricesResult = await user.functions.price_getB2B(pricesPayload);

  itemPricesArray.forEach((itemPrice) => {
    const { dataset } = itemPrice;
    const { sku, variants } = dataset;
    const findPrice = itemPricesResult.find((p) => p.ProductCode === sku);
    if (findPrice) {
      const individualPrice = findPrice.SellingPrice;
      const priceText =
        parseInt(variants) > 1
          ? `$${individualPrice.toFixed(2)} (Varies)`
          : `$${individualPrice.toFixed(2)}`;
      if (individualPrice <= 0) {
        itemPrice.innerText = "Contact Us";
        // Find the corresponding quantity input and button elements
        const qtyInput = document.getElementById(`${sku}QTY`);
        const addToCartButton = qtyInput.nextElementSibling;
        // Remove the quantity input and button elements from the DOM
        qtyInput.parentNode.removeChild(qtyInput);
        addToCartButton.parentNode.removeChild(addToCartButton);
      } else {
        itemPrice.innerText = priceText;
      }

      itemPrice.classList.remove("hidden");
    }
  });
  await rapido_calculateAndUpdateCart();

  // Apply price visibility settings after price updates
  updatePricesVisibility();
}

async function syncTyreStewardshipFee({
  tyreFeeItem,
  expectedQty,
  isCartPage,
}) {
  if (tyreFeeSyncState.inProgress) {
    return;
  }

  if (tyreFeeSyncState.desiredQty !== expectedQty) {
    tyreFeeSyncState.attempts = 0;
    tyreFeeSyncState.failureLogged = false;
  } else if (tyreFeeSyncState.attempts >= 3) {
    if (!tyreFeeSyncState.failureLogged) {
      console.warn(
        "Skipping tyre fee synchronisation after repeated failures",
        { expectedQty }
      );
      tyreFeeSyncState.failureLogged = true;
    }

    try {
      const checkoutButton = document.querySelector("#checkout_rapido");
      if (checkoutButton) {
        checkoutButton.disabled = false;
      }
    } catch (error) {}

    return;
  }

  tyreFeeSyncState.inProgress = true;
  tyreFeeSyncState.desiredQty = expectedQty;
  tyreFeeSyncState.attempts += 1;

  const checkoutButton = document.querySelector("#checkout_rapido");
  if (checkoutButton) {
    checkoutButton.disabled = true;
  }

  try {
    let response;

    if (!tyreFeeItem) {
      response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: TYRE_FEE_VARIANT_ID,
          quantity: expectedQty,
        }),
      });
    } else {
      response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: tyreFeeItem.key,
          quantity: expectedQty,
        }),
      });
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        "Failed to synchronise tyre stewardship fee line item:",
        response.status,
        errorText
      );

      if (checkoutButton) {
        checkoutButton.disabled = false;
      }

      return;
    }

    tyreFeeSyncState.attempts = 0;
    tyreFeeSyncState.failureLogged = false;

    if (isCartPage) {
      if (checkoutButton) {
        checkoutButton.disabled = false;
      }
      window.location.reload();
    } else {
      const sidebarCart = document.querySelector("#sidebar-cart");
      if (
        sidebarCart &&
        typeof theme !== "undefined" &&
        typeof theme.Cart === "function"
      ) {
        const myCart = new theme.Cart(sidebarCart);
        await myCart._rerenderCart();
      }
      if (checkoutButton) {
        checkoutButton.disabled = false;
      }
    }
  } catch (error) {
    console.error("Error synchronising tyre stewardship fee:", error);
    if (checkoutButton) {
      checkoutButton.disabled = false;
    }
  } finally {
    tyreFeeSyncState.inProgress = false;
  }
}

async function rapido_calculateAndUpdateCart() {
  const user = await rapido_getRealmUser();

  if (user.id === "-1") {
    return;
  }

  const cartResponse = await fetch("/cart.js");
  const cart = await cartResponse.json();
  const cartItems = cart.items;

  const isCartPage = window.theme && window.theme.pageType === "cart";

  // Hide all price elements immediately while calculating
  const allPriceElements = document.querySelectorAll('.rapido-line-price, .rapido-subtotal, .rapido-freight-amount, .rapido-taxamount, .rapido-grosstotal');
  allPriceElements.forEach(elem => {
    elem.classList.add('hidden');
    elem.setAttribute('data-loading', 'true');
  });

  // If no items exist, reset all summary fields to $0 and exit early
  if (cartItems.length === 0) {
    const zero = "$0.00";
    const freightElem = document.getElementsByClassName(
      "rapido-freight-amount"
    )[0];
    if (freightElem) {
      freightElem.innerText = zero;
      freightElem.removeAttribute('data-loading');
      freightElem.classList.remove("hidden");
    }
    const subTotals = document.getElementsByClassName("rapido-subtotal");
    [...subTotals].forEach((subTotal) => {
      subTotal.innerText = zero;
      subTotal.removeAttribute('data-loading');
      subTotal.classList.remove("hidden");
    });
    const taxAmount = document.getElementsByClassName("rapido-taxamount")[0];
    if (taxAmount) {
      taxAmount.innerText = zero;
      taxAmount.removeAttribute('data-loading');
      taxAmount.classList.remove("hidden");
    }
    const totalElem = document.getElementsByClassName("rapido-grosstotal")[0];
    if (totalElem) {
      totalElem.innerText = zero;
      totalElem.removeAttribute('data-loading');
      totalElem.classList.remove("hidden");
    }
    return;
  }

  // Track if any WHEEL items are missing vehicle properties
  let hasMissingVehicleDetails = false;

  if (isCartPage) {
    // Add vehicle property editor for WHEEL items missing properties
    cartItems.forEach((item) => {
      const properties = item.properties || {};
      const hasVehicleProperties =
        properties.Make &&
        properties.Model &&
        properties.Year &&
        properties.Vehicle;

      // Check for YMM waiver (Shopify stores properties as strings)
      const hasYmmWaiver =
        properties.ymm_waived === true || properties.ymm_waived === "true";

      // Only add selector if neither vehicle properties nor waiver is present
      if (
        !hasVehicleProperties &&
        !hasYmmWaiver &&
        item.product_type === "WHEEL"
      ) {
        hasMissingVehicleDetails = true;
        const lineItemElem = document
          .querySelector(`[data-line-id="${item.key}"]`)
          ?.closest(".CartItem");
        if (lineItemElem) {
          // Only add selector if it doesn't already exist
          if (!lineItemElem.querySelector(".vehicle-selector")) {
            lineItemElem
              .querySelector(".CartItem__Info")
              .insertAdjacentHTML(
                "beforeend",
                getVehicleSelectorHTML(item.key)
              );

            // Initialize vehicle selector dropdowns
            initializeVehicleSelector(item.key);
          }
        }
      }
    });
  }

  if (isCartPage) {
    // Disable checkout button if there are missing vehicle details
    const checkoutButton = document.getElementById("checkout_rapido");
    if (checkoutButton) {
      if (hasMissingVehicleDetails) {
        checkoutButton.disabled = true;
        checkoutButton.title =
          "Please select vehicle details for all wheel items";
        // Add a message above the checkout button
        let messageElem = document.getElementById("vehicle-details-message");
        if (!messageElem) {
          messageElem = document.createElement("div");
          messageElem.id = "vehicle-details-message";
          messageElem.className = "vehicle-details-warning";
          checkoutButton.parentElement.insertBefore(
            messageElem,
            checkoutButton
          );
        }
        messageElem.innerHTML =
          "Please select vehicle details for all wheel items before checkout";
      } else {
        checkoutButton.disabled = false;
        checkoutButton.title = "";
        // Remove the message if it exists
        const messageElem = document.getElementById("vehicle-details-message");
        if (messageElem) {
          messageElem.remove();
        }
      }
    }
  }

  const linePrices = document.getElementsByClassName("rapido-line-price");

  // Group items by SKU and sum their quantities
  const groupedItems = cartItems.reduce((acc, item) => {
    const sku = item.sku;
    if (!acc[sku]) {
      acc[sku] = { ...item, totalQuantity: 0 };
    }
    acc[sku].totalQuantity += item.quantity;
    return acc;
  }, {});

  const pricesPayload = Object.values(groupedItems).map((item) => ({
    ProductCode: item.sku,
    Quantity: item.totalQuantity.toString(),
  }));

  const linePricesResult = await user.functions.price_getB2B(pricesPayload);

  [...linePrices].forEach((linePrice) => {
    const { dataset } = linePrice;
    const { sku, quantity } = dataset;
    const findPrice = linePricesResult.find((p) => p.ProductCode === sku);
    if (findPrice) {
      const individualPrice = findPrice.SellingPrice;
      const qty = parseInt(quantity);
      if (linePrice.classList.contains("rapido-line-price-single")) {
        linePrice.innerText = `$${individualPrice.toFixed(2)}`;
      } else {
        const lineTotal = individualPrice * qty;
        linePrice.innerText = `$${lineTotal.toFixed(2)}`;
      }
      // Only show price if we have a valid price greater than 0 or if it's legitimately 0
      if (individualPrice >= 0) {
        linePrice.removeAttribute('data-loading');
        linePrice.classList.remove("hidden");
      }
    }
  });

  let subTotalValue = linePricesResult.reduce((acc, curr) => {
    const { SellingPrice, Quantity } = curr;
    const qty = parseInt(Quantity);
    const lineTotal = SellingPrice * qty;
    return acc + lineTotal;
  }, 0);

  const tyreItems = cartItems.filter(
    (item) =>
      item.product_type === "TYRE" && item.variant_id !== TYRE_FEE_VARIANT_ID
  );
  const totalTyreQty = tyreItems.reduce(
    (total, item) => total + Number(item.quantity ?? 0),
    0
  );
  const wheelItems = cartItems.filter((item) => item.product_type === "WHEEL");
  const totalWheelQty = wheelItems.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const wheelAndTyreQty = totalTyreQty + totalWheelQty;
  let freightCost = 0;
  if (wheelAndTyreQty === 1) {
    freightCost = 15;
  } else if (wheelAndTyreQty === 0) {
    freightCost = 12.5;
  }

  subTotalValue += freightCost;

  const freightAmount = document.getElementsByClassName(
    "rapido-freight-amount"
  )[0];
  if (freightAmount && freightCost >= 0) {
    freightAmount.innerText = `$${freightCost.toFixed(2)}`;
    freightAmount.removeAttribute('data-loading');
    freightAmount.classList.remove("hidden");
  }

  const subTotals = document.getElementsByClassName("rapido-subtotal");
  if (subTotalValue >= 0) {
    [...subTotals].forEach((subTotal) => {
      subTotal.innerText = `$${subTotalValue.toFixed(2)}`;
      subTotal.removeAttribute('data-loading');
      subTotal.classList.remove("hidden");
    });
  }

  const taxAmount = document.getElementsByClassName("rapido-taxamount")[0];
  if (taxAmount && subTotalValue >= 0) {
    const taxRate = 0.15;
    const taxAmountValue = subTotalValue * taxRate;
    // Calculate rounded up tax to two decimal places
    const roundedUpTax = Math.ceil(taxAmountValue * 100) / 100;
    taxAmount.innerText = `$${roundedUpTax.toFixed(2)}`;
    taxAmount.removeAttribute('data-loading');
    taxAmount.classList.remove("hidden");
  }

  const total = document.getElementsByClassName("rapido-grosstotal")[0];
  if (total && subTotalValue >= 0) {
    const taxRate = 0.15; // This represents a 15% tax rate, adjust if different
    const totalValue = subTotalValue * (1 + taxRate); // Calculate total with tax
    // Calculate rounded up total to two decimal places
    const roundedUpTotal = Math.ceil(totalValue * 100) / 100;
    total.innerText = `$${roundedUpTotal.toFixed(2)}`;
    total.removeAttribute('data-loading');
    total.classList.remove("hidden");
  }

  // Tyre fee check
  // The stewardship fee should mirror the summed tyre quantity across all variants
  const expectedTyreQty = totalTyreQty;

  const tyreFeeItem = cartItems.find(
    (item) => item.variant_id === TYRE_FEE_VARIANT_ID
  );

  if (totalTyreQty === 0 && tyreFeeItem) {
    tyreFeeSyncState.desiredQty = 0;
    tyreFeeSyncState.attempts = 0;
    tyreFeeSyncState.failureLogged = false;
    tyreFeeSyncState.inProgress = false;

    // Remove the tyre fee variant from the cart if there are no tyre items
    await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: tyreFeeItem.key,
        quantity: 0,
      }),
    });
    try {
      document.querySelector("#checkout_rapido").disabled = false;
    } catch (error) {}
    window.location.reload();
  } else if (
    totalTyreQty > 0 &&
    (!tyreFeeItem || tyreFeeItem.quantity !== expectedTyreQty)
  ) {
    await syncTyreStewardshipFee({
      tyreFeeItem,
      expectedQty: expectedTyreQty,
      isCartPage,
    });
  } else {
    // Enable the place order button
    try {
      document.querySelector("#checkout_rapido").disabled = false;
    } catch (error) {}

    tyreFeeSyncState.desiredQty = expectedTyreQty;
    tyreFeeSyncState.attempts = 0;
    tyreFeeSyncState.failureLogged = false;
    tyreFeeSyncState.inProgress = false;
  }

  // Apply price visibility settings after cart calculation
  updatePricesVisibility();

  // Enhance YMM properties display after cart updates
  if (typeof enhanceYmmProperties === "function") {
    enhanceYmmProperties();
  }

  // Reattach checkout handler after cart updates
  if (typeof attachCheckoutHandler === "function") {
    setTimeout(() => {
      attachCheckoutHandler();
    }, 200);
  }
}

async function initializeVehicleSelector(lineId) {
  const makeSelect = document.getElementById(`dd-make-${lineId}`);
  const modelSelect = document.getElementById(`dd-model-${lineId}`);

  try {
    // Get and populate makes
    const makes = await rapido_getMakes();
    if (!Array.isArray(makes)) {
      console.error("Makes data is not an array:", makes);
      return;
    }

    makeSelect.innerHTML = '<option value="">Select Make</option>';
    makes.sort().forEach((make) => {
      const option = document.createElement("option");
      option.value = make;
      option.textContent = make;
      makeSelect.appendChild(option);
    });

    // Add change handler for make
    makeSelect.addEventListener("change", async () => {
      const selectedMake = makeSelect.value;
      if (!selectedMake) {
        // Reset and disable dependent dropdowns
        modelSelect.innerHTML = '<option value="">Select Model</option>';
        modelSelect.disabled = true;
        return;
      }

      try {
        const ymmResponse = await rapido_getYMM(selectedMake);

        // Enable and populate model dropdown
        modelSelect.disabled = false;
        modelSelect.innerHTML = '<option value="">Select Model</option>';

        // Get unique models and sort
        const models = [
          ...new Set(ymmResponse.models.map((item) => item.model)),
        ].sort();

        models.forEach((model) => {
          const option = document.createElement("option");
          option.value = model;
          option.textContent = model;
          modelSelect.appendChild(option);
        });

        // Add change handler for model
        modelSelect.addEventListener("change", () => {
          const selectedModel = modelSelect.value;
          if (selectedModel) {
            updateYearDropdown(lineId, ymmResponse.models, selectedModel);
          }
        });
      } catch (error) {
        console.error("Error fetching YMM data:", error);
        modelSelect.innerHTML =
          '<option value="">Error loading models</option>';
        modelSelect.disabled = true;
      }
    });
  } catch (error) {
    console.error("Error initializing vehicle selector:", error);
    makeSelect.innerHTML = '<option value="">Error loading makes</option>';
    makeSelect.disabled = true;
  }
}

async function updateYearDropdown(lineId, models, selectedModel) {
  const yearSelect = document.getElementById(`dd-year-${lineId}`);
  const vehicleSelect = document.getElementById(`dd-vehicle-${lineId}`);

  yearSelect.disabled = false;
  yearSelect.innerHTML = '<option value="">Select Year</option>';

  try {
    // Find the selected model's data
    const modelData = models.find((item) => item.model === selectedModel);
    if (!modelData || !modelData.years) {
      throw new Error("Invalid model data structure");
    }

    // Get years from the model data
    const years = modelData.years
      .map((item) => item.year)
      .sort()
      .reverse();

    years.forEach((year) => {
      const option = document.createElement("option");
      option.value = year;
      option.textContent = year;
      yearSelect.appendChild(option);
    });

    // Reset and disable vehicle select
    vehicleSelect.innerHTML = '<option value="">Select Vehicle</option>';
    vehicleSelect.disabled = true;

    yearSelect.addEventListener("change", () => {
      updateVehicleDropdown(
        lineId,
        modelData.years,
        selectedModel,
        yearSelect.value
      );
    });
  } catch (error) {
    console.error("Error updating year dropdown:", error);
    yearSelect.innerHTML = '<option value="">Error loading years</option>';
    yearSelect.disabled = true;
  }
}

async function updateVehicleDropdown(
  lineId,
  years,
  selectedModel,
  selectedYear
) {
  const vehicleSelect = document.getElementById(`dd-vehicle-${lineId}`);

  try {
    vehicleSelect.disabled = false;
    vehicleSelect.innerHTML = '<option value="">Select Vehicle</option>';

    // Find the selected year's data
    const yearData = years.find((item) => item.year === selectedYear);
    if (!yearData || !yearData.vehicles) {
      throw new Error("Invalid year data structure");
    }

    // Get vehicles for the selected year, keeping the VehicleID so the
    // accessory guide can be matched in Accredo
    const vehicles = [...yearData.vehicles].sort((a, b) => {
      const left = String(a.VehicleType);
      const right = String(b.VehicleType);
      return left < right ? -1 : left > right ? 1 : 0;
    });

    vehicles.forEach((vehicle) => {
      const option = document.createElement("option");
      option.value = vehicle.VehicleType;
      option.textContent = vehicle.VehicleType;
      if (vehicle.VehicleID) {
        option.dataset.vehicleId = vehicle.VehicleID;
      }
      vehicleSelect.appendChild(option);
    });
  } catch (error) {
    console.error("Error updating vehicle dropdown:", error);
    vehicleSelect.innerHTML =
      '<option value="">Error loading vehicles</option>';
    vehicleSelect.disabled = true;
  }
}

async function rapido_updateLineItemProperties(lineId) {
  // Check if YMM is waived
  const ymmWaived = document.getElementById(`ymm-waived-${lineId}`)?.checked;

  let properties = {};

  if (ymmWaived) {
    // If YMM is waived, set the waiver property only
    properties = {
      ymm_waived: true,
    };
  } else {
    // Check if using manual entry
    const isManualEntry = document.getElementById(
      `manual-entry-${lineId}`
    )?.checked;

    if (isManualEntry) {
      // Get values from manual input fields
      const make = document.getElementById(`manual-make-${lineId}`).value;
      const model = document.getElementById(`manual-model-${lineId}`).value;
      const year = document.getElementById(`manual-year-${lineId}`).value;
      const vehicle = document.getElementById(`manual-vehicle-${lineId}`).value;

      if (!make || !model || !year || !vehicle) {
        alert(
          "Please fill in all vehicle details or check 'YMM Not Applicable'"
        );
        return;
      }

      properties = {
        Make: make,
        Model: model,
        Year: year,
        Vehicle: vehicle,
      };
    } else {
      // Otherwise get regular YMM properties from dropdowns
      const make = document.getElementById(`dd-make-${lineId}`).value;
      const model = document.getElementById(`dd-model-${lineId}`).value;
      const year = document.getElementById(`dd-year-${lineId}`).value;
      const vehicle = document.getElementById(`dd-vehicle-${lineId}`).value;
      const vehicleId = getSelectedVehicleId(`dd-vehicle-${lineId}`);

      if (!make || !model || !year || !vehicle) {
        alert(
          "Please select all vehicle details or check 'YMM Not Applicable'"
        );
        return;
      }

      properties = {
        Make: make,
        Model: model,
        Year: year,
        Vehicle: vehicle,
        ...(vehicleId ? { _VehicleID: String(vehicleId) } : {}),
      };
    }
  }

  try {
    // Step 1: Fetch the current cart to get the real quantity
    const cartResponse = await fetch("/cart.js");
    const cart = await cartResponse.json();

    // Step 2: Find the line item by key (lineId)
    const lineItem = cart.items.find((item) => item.key === lineId);

    if (!lineItem) {
      console.error("Line item not found in cart:", lineId);
      alert(
        "Error: Could not find item in cart. Please refresh and try again."
      );
      return;
    }

    const currentQuantity = lineItem.quantity;
    console.log(
      "Updating vehicle properties for line",
      lineId,
      "with quantity:",
      currentQuantity
    );

    // Step 3: Update the line item properties and preserve quantity
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: lineId.toString(), // Convert to string as required by Shopify cart API
        quantity: currentQuantity, // Preserve the current quantity from cart
        properties: properties,
      }),
    });

    if (response.ok) {
      console.log("Vehicle properties updated successfully");

      // Refresh cart display
      const myCart = new theme.Cart(document.querySelector("#sidebar-cart"));
      await myCart._rerenderCart();

      // Apply price visibility settings after cart refresh
      setTimeout(() => {
        updatePricesVisibility();
        // Enhance YMM properties display
        if (typeof enhanceYmmProperties === "function") {
          enhanceYmmProperties();
        }
      }, 100);
    } else {
      const errorText = await response.text();
      console.error("Error updating vehicle properties:", errorText);
      alert("Failed to update vehicle properties. Please try again.");
    }
  } catch (error) {
    console.error("Error in rapido_updateLineItemProperties:", error);
    alert(
      "An error occurred while updating vehicle properties. Please try again."
    );
  }
}

function calculateExpectedTyreQty(cartItems) {
  // Calculate the total quantity of tyre items in the cart
  const totalTyreQty = cartItems.reduce((total, item) => {
    if (item.product_type === "TYRE") {
      return total + item.quantity;
    }
    return total;
  }, 0);

  return totalTyreQty;
}

async function getProductsOrderInventory(productCodes) {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.bgm_returnOrderInventory(
      productCodes
    );
  });
}

async function getPageFitmentsYMM(lineId, vehicleId, shopifyProductId) {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.bgm_getPageFitmentsMMY(
      lineId,
      vehicleId,
      shopifyProductId
    );
  });
}

async function getGalleryMakeModels() {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.bgm_getGalleryMakeModel();
  });
}

async function rapido_getCustomerInfo() {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.customer_getInfo();
  });
}

async function rapido_getCustomerBalance() {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.getCustomerBalance();
  });
}

async function getGalleryProducts(skus) {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.bgm_getGalleryProduct(skus);
  });
}

async function getGalleryImages(make, model, limit, skip) {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.bgm_getGalleryImages(
      make,
      model,
      limit,
      skip
    );
  });
}

async function getMappedProducts(productCodes) {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.bgm_mapShopifyProds(productCodes);
  });
}

async function getOtherFinishes(shopifyProductId) {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.bgm_getOtherFinishes(
      shopifyProductId
    );
  });
}

async function rapido_createInvoiceOrder(cart) {
  const checkoutBtn = document.getElementById("checkout_rapido");
  const cartNote = document.getElementById("CartSpecialInstructions").value;
  // Add loading class to btn
  checkoutBtn.classList.add("loading");
  checkoutBtn.disabled = true;

  const draftOrder = await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.checkout_invoiceOrder(cart, cartNote);
  });
  // Remove checkout button
  checkoutBtn.classList.add("hidden");
  return draftOrder;
}

const getSelectedOptionText = (id) => {
  const element = document.getElementById(id);
  if (!element) {
    return "";
  }

  const selectedOption =
    element.selectedOptions?.[0] || element.options?.[element.selectedIndex];
  const selectedText = selectedOption?.textContent?.trim();

  if (selectedText) {
    return selectedText;
  }

  return element.value ?? "";
};

// Accredo's accessory guide is keyed on VehicleID, not the display text, so
// carry it through on the line item wherever we genuinely know it
const getSelectedVehicleId = (id) => {
  const element = document.getElementById(id);
  const selectedOption =
    element?.selectedOptions?.[0] || element?.options?.[element?.selectedIndex];
  return selectedOption?.dataset?.vehicleId || "";
};

async function rapido_addToCart(variantId, quantity, showUpSell) {
  const fitmentDetails = parseFitmentDataFromURL(window.location);
  // Prefer the live picker over the URL: on the fitment page the dropdown is
  // always the current selection, while the URL may still hold an older one
  const vehicleId =
    getSelectedVehicleId("dd-vehicle") || fitmentDetails?.vehicleId;

  const addToCart = await fetch(window.Shopify.routes.root + "cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id: variantId,
      quantity: quantity,
      properties: {
        Make: fitmentDetails?.make || getSelectedOptionText("dd-make"),
        Model: fitmentDetails?.model || getSelectedOptionText("dd-model"),
        Year: fitmentDetails?.year || getSelectedOptionText("dd-year"),
        Vehicle:
          fitmentDetails?.vehicleType || getSelectedOptionText("dd-vehicle"),
        ...(vehicleId ? { _VehicleID: String(vehicleId) } : {}),
      },
    }),
  });

  const response = await addToCart.json();

  // Update the cart count number in the header
  try {
    const myCart = new theme.Cart(document.querySelector("#sidebar-cart"));
    await myCart._rerenderCart();
    await myCart._sidebarDrawerOpen();

    // Enhance YMM properties display after adding to cart
    setTimeout(() => {
      if (typeof enhanceYmmProperties === "function") {
        enhanceYmmProperties();
      }
    }, 100);
  } catch (error) {
    console.log("Couldn't update cart count");
  }

  // Check if YMM exists in localStorage
  const ymmData = parseFitmentDataFromURL(window.location);
  if (ymmData && showUpSell) {
    try {
      // Extract productCodes attribute
      const { lineId, vehicleId } = ymmData;

      // Call getMappedProducts with the extracted productCodes
      if (lineId) {
        const user = await rapido_getRealmUser();
        const tyres = await user.functions.bgm_getFitmentsMMYUpSell(
          lineId,
          vehicleId
        );

        // Show the modal and insert JSON response into the modal__inner div
        try {
          if (tyres.products && tyres.products.length > 0) {
            const modal = document.getElementById("modal-quick-view");
            modal.setAttribute("aria-hidden", "false");
            const modalInner = modal.querySelector(".modal__inner");
            modalInner.innerHTML = `
              <header class="section-header text-center homepage_subtitle_style_primary">
                <h2>Need some tyres?</h2>
                <p><strong>Vehicle:</strong> ${ymmData.make} ${ymmData.model} ${
              ymmData.year
            }</p>
              </header>
              ${createProductGrid(tyres.products)}
            `;
            await rapido_updatePrices();
          }
        } catch (error) {
          console.error("Failed to update modal content", error);
        }
      }
    } catch (error) {
      console.error("Failed to parse YMM data from URL", error);
    }
  }

  return response;
}

function createProductGrid(products) {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const createQuantityAndButton = (variant) => `
    <div class="quantity-and-button">
      <input class="matrix_input quantity-input" id="${variant.sku}QTY" type="number" min="1" value="1">
      <button onclick="rapido_addToCart(${variant.variantId}, parseInt(document.getElementById('${variant.sku}QTY').value), false)" type="submit" name="add" class="product-form__submit btn btn--small btn--primary">
        <span>Add</span>
      </button>
    </div>
  `;

  if (isMobile) {
    const createMobileGrid = products
      .map((product) => {
        const variant = product.variants[0];
        return `
        <div class="mobile-grid" id="MOB" data-sku="${variant.sku}">
          <span>
            <img src="${product.variantImage}" alt="${product.Brand} ${
          product.Style
        }" width="100" height="100">
          </span>
          <span>
            <b>Brand:</b> ${product.Brand}<br>
            <b>Part Number:</b>
            <a href="${variant.productLink}">${variant.sku}</a>
          </span>
          <span>${
            variant.option1 === variant.sku ? "" : variant.option1 || "-"
          }</span>
          <span>${
            variant.option2 === variant.sku ? "" : variant.option2 || "-"
          }</span>
          <span>${
            variant.option3 === variant.sku ? "" : variant.option3 || "-"
          }</span>
          <span>
            <b>Stock:</b>
            ${variant.inventoryQuantity || "-"}
          </span>
          <span>
            <strong class="rapido-item-price" data-sku="${variant.sku}">${
          variant.price || "P.O.A"
        }</strong>
          </span>
          <span>
            ${createQuantityAndButton(variant)}
          </span>
        </div>
      `;
      })
      .join("");

    return `
      <section data-section-type="featured-products">
        <div class="index-tabs-collections-wrapper section_main_content page-width" style="margin-top:20px">
          <div class="mobile_matrix page-width">
            ${createMobileGrid}
          </div>
        </div>
      </section>
      <style>
        .quantity-and-button {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 10px;
        }
        .quantity-input {
          width: 100%;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 16px;
        }
      </style>
    `;
  } else {
    const createTableRows = products
      .map((product) => {
        const variant = product.variants[0];
        return `
        <tr>
          <td>
            <img src="${product.variantImage}" alt="${product.Brand} ${
          product.Style
        }" width="100" height="100">
          </td>
          <td>
            <b>${product.Brand}</b><br>
            <a href="${variant.productLink}">${variant.sku}</a>
          </td>
          <td>${
            variant.option1 === variant.sku ? "" : variant.option1 || "-"
          }</td>
          <td>${
            variant.option2 === variant.sku ? "" : variant.option2 || "-"
          }</td>
          <td>${
            variant.option3 === variant.sku ? "" : variant.option3 || "-"
          }</td>
          <td>${variant.inventoryQuantity || "-"}</td>
          <td>
            <span class="rapido-item-price" data-sku="${variant.sku}">${
          variant.price || "P.O.A"
        }</span>
          </td>
          <td>
            ${createQuantityAndButton(variant)}
          </td>
        </tr>
      `;
      })
      .join("");

    return `
      <section data-section-type="featured-products">
        <div class="index-tabs-collections-wrapper section_main_content page-width" style="margin-top:20px">
          <table class="spec-table desktop_matrix" id="variantTable">
            <thead>
              <tr>
                <th>Image</th>
                <th>Brand & Part Number</th>
                <th></th>
                <th></th>
                <th></th>
                <th>Stock</th>
                <th>Price</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${createTableRows}
            </tbody>
          </table>
        </div>
      </section>
      <style>
        .quantity-and-button {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .quantity-input {
          width: 60px;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
        }
      </style>
    `;
  }
}

function parseFitmentDataFromURL(url) {
  try {
    const parsedUrl = new URL(url);
    const params = new URLSearchParams(parsedUrl.search);

    const lineId = params.get("lineId");
    const vehicleId = params.get("vehicleId");
    const year = params.get("year");
    const make = params.get("make");
    const model = params.get("model");
    const vehicleType = params.get("vehicleType");

    if (lineId && vehicleId && year && make && model && vehicleType) {
      return {
        lineId: parseInt(lineId, 10),
        vehicleId: parseInt(vehicleId, 10),
        year,
        make,
        model,
        vehicleType,
      };
    }

    return null; // Return null if any required parameter is missing
  } catch (error) {
    console.error("Error parsing URL:", error);
    return null;
  }
}

document.addEventListener("DOMContentLoaded", () => {
  if (window.location.pathname === "/account") {
    rapido_getCustomerInfo()
      .then((userInfo) => {
        console.log(userInfo);
        // Find .h1 mega-title slideshow__title
        const megaTitle = document.querySelector(
          ".h1.mega-title.slideshow__title"
        );
        if (!megaTitle) {
          console.warn("Could not find mega title element");
          return;
        }
        let name = "";
        if (userInfo.DealerCode) {
          if (userInfo.DealerName) {
            name = `${userInfo.DealerName}`;
          } else {
            name = `${userInfo.Name} (${userInfo.DealerCode})`;
          }
        } else {
          name = userInfo.Name;
        }
        // Add element below megaTitle
        const text = `
          <h2 class="slideshow__title">${name}</h2>
        `;
        megaTitle.insertAdjacentHTML("afterend", text);
        // Set margin bottom to 0 on .slideshow__text-wrap--desktop .slideshow__text-content .slideshow__title
        const title = document.querySelector(
          ".slideshow__text-wrap--desktop .slideshow__text-content .slideshow__title"
        );
        if (title) {
          title.style.marginBottom = "0";
        }
      })
      .catch((error) => {
        console.error('Failed to get customer info:', error);
        if (error.message && error.message.includes('Realm SDK failed to load')) {
          console.error('Page is still loading. Please refresh if this persists.');
        }
      });
  }
});

async function rapido_getDeliveryAddresses() {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.customer_getDeliveryAddresses();
  });
}

// Add these functions at the top level, near other similar functions
async function rapido_getMakes() {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    return await user.functions.bgm_getMakes();
  });
}

async function rapido_getYMM(make) {
  return await executeRealmFunction(async () => {
    const user = await rapido_getRealmUser();
    const ymm = await user.functions.bgm_getMMY(make);
    console.log("Raw YMM response:", ymm); // Add this line
    return ymm;
  });
}

// Update the vehicle selector HTML with manual entry option
function getVehicleSelectorHTML(lineId) {
  return `
    <div class="vehicle-selector" data-line-id="${lineId}">
      <div class="vehicle-selector__header">
        <h3 class="vehicle-selector__title">Select Vehicle Details</h3>
      </div>
      
      <div class="vehicle-selector__waiver">
        <div class="vehicle-selector__waiver-row">
          <label class="vehicle-selector__waiver-label">
            <input type="checkbox" id="ymm-waived-${lineId}" class="vehicle-selector__waiver-checkbox" onchange="toggleYmmWaiver('${lineId}')">
            <span>YMM Not Applicable</span>
          </label>
          <button onclick="event.preventDefault(); rapido_saveYmmWaiver('${lineId}')" id="waiver-save-${lineId}" class="waiver-save-btn btn btn--small" style="display:none;">
            Save
          </button>
        </div>
        <p class="vehicle-selector__waiver-help">Check this if vehicle details don't apply to this item</p>
      </div>
      
      <div class="vehicle-selector__toggle">
        <label class="vehicle-selector__toggle-label">
          <input type="checkbox" id="manual-entry-${lineId}" class="vehicle-selector__toggle-checkbox" onchange="toggleManualEntry('${lineId}')">
          <span>Enter Vehicle Details Manually</span>
        </label>
      </div>
      
      <!-- Dropdown Selector (Default View) -->
      <div id="dropdown-fields-${lineId}" class="vehicle-selector__fields">
        <div class="vehicle-selector__field">
          <label for="dd-make-${lineId}">Make</label>
          <select id="dd-make-${lineId}" class="dd-make">
            <option value="">Select Make</option>
          </select>
        </div>
        <div class="vehicle-selector__field">
          <label for="dd-model-${lineId}">Model</label>
          <select id="dd-model-${lineId}" class="dd-model" disabled>
            <option value="">Select Model</option>
          </select>
        </div>
        <div class="vehicle-selector__field">
          <label for="dd-year-${lineId}">Year</label>
          <select id="dd-year-${lineId}" class="dd-year" disabled>
            <option value="">Select Year</option>
          </select>
        </div>
        <div class="vehicle-selector__field">
          <label for="dd-vehicle-${lineId}">Vehicle</label>
          <select id="dd-vehicle-${lineId}" class="dd-vehicle" disabled>
            <option value="">Select Vehicle</option>
          </select>
        </div>
      </div>
      
      <!-- Manual Entry Fields (Hidden by Default) -->
      <div id="manual-fields-${lineId}" class="vehicle-selector__fields" style="display: none;">
        <div class="vehicle-selector__field">
          <label for="manual-make-${lineId}">Make</label>
          <input type="text" id="manual-make-${lineId}" class="manual-make" placeholder="Enter vehicle make">
        </div>
        <div class="vehicle-selector__field">
          <label for="manual-model-${lineId}">Model</label>
          <input type="text" id="manual-model-${lineId}" class="manual-model" placeholder="Enter vehicle model">
        </div>
        <div class="vehicle-selector__field">
          <label for="manual-year-${lineId}">Year</label>
          <input type="text" id="manual-year-${lineId}" class="manual-year" placeholder="Enter vehicle year">
        </div>
        <div class="vehicle-selector__field">
          <label for="manual-vehicle-${lineId}">Vehicle</label>
          <input type="text" id="manual-vehicle-${lineId}" class="manual-vehicle" placeholder="Enter vehicle type">
        </div>
      </div>
      
      <div class="vehicle-selector__actions">
        <button onclick="rapido_updateLineItemProperties('${lineId}')" class="btn btn--small">
          Update Vehicle
        </button>
      </div>
    </div>
    <style>
      .vehicle-selector {
        margin: 15px 0;
        padding: 15px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: #f9f9f9;
      }
      .vehicle-selector__header {
        margin-bottom: 15px;
      }
      .vehicle-selector__title {
        font-size: 16px;
        margin: 0;
        color: #333;
      }
      .vehicle-selector__waiver {
        margin-bottom: 15px;
        padding-bottom: 10px;
        border-bottom: 1px solid #eee;
      }
      .vehicle-selector__waiver-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .vehicle-selector__waiver-label {
        display: flex;
        align-items: center;
        font-weight: bold;
        cursor: pointer;
      }
      .vehicle-selector__waiver-checkbox {
        margin-right: 8px;
      }
      .vehicle-selector__waiver-help {
        margin: 5px 0 0 24px;
        font-size: 12px;
        color: #666;
      }
      .vehicle-selector__toggle {
        margin-bottom: 15px;
      }
      .vehicle-selector__toggle-label {
        display: flex;
        align-items: center;
        font-weight: bold;
        cursor: pointer;
      }
      .vehicle-selector__toggle-checkbox {
        margin-right: 8px;
      }
      .waiver-save-btn {
        padding: 4px 12px;
        font-size: 12px;
        background: #4a8;
        color: white;
      }
      .vehicle-selector__fields {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 10px;
        margin-bottom: 15px;
      }
      .vehicle-selector__field {
        display: flex;
        flex-direction: column;
      }
      .vehicle-selector__field label {
        font-size: 14px;
        margin-bottom: 5px;
        color: #666;
      }
      .vehicle-selector__field select,
      .vehicle-selector__field input {
        width: 100%;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
        font-size: 14px;
      }
      .vehicle-selector__field select:disabled {
        background: #f5f5f5;
        cursor: not-allowed;
      }
      .vehicle-selector__actions {
        display: flex;
        justify-content: flex-end;
      }
      .vehicle-selector__actions button {
        min-width: 120px;
      }
      @media (max-width: 768px) {
        .vehicle-selector__fields {
          grid-template-columns: 1fr;
        }
        .vehicle-selector__actions {
          justify-content: stretch;
        }
        .vehicle-selector__actions button {
          width: 100%;
        }
      }
      
      /* Style for waived YMM in the cart property list */
      .CartItem__Property.ymm-waived-property {
        background: #e8ffe8;
        padding: 5px 10px;
        border-radius: 4px;
        border-left: 3px solid #4a8;
        margin-bottom: 8px;
        color: #2a6;
        font-weight: 500;
      }
    </style>
  `;
}

// Function to toggle between dropdown and manual entry fields
function toggleManualEntry(lineId) {
  const manualEntryCheckbox = document.getElementById(`manual-entry-${lineId}`);
  const dropdownFields = document.getElementById(`dropdown-fields-${lineId}`);
  const manualFields = document.getElementById(`manual-fields-${lineId}`);

  if (manualEntryCheckbox.checked) {
    // Show manual fields, hide dropdown fields
    dropdownFields.style.display = "none";
    manualFields.style.display = "grid";
  } else {
    // Show dropdown fields, hide manual fields
    dropdownFields.style.display = "grid";
    manualFields.style.display = "none";
  }
}

// Function to toggle YMM waiver checkbox
function toggleYmmWaiver(lineId) {
  const ymmWaivedCheckbox = document.getElementById(`ymm-waived-${lineId}`);
  const saveButton = document.getElementById(`waiver-save-${lineId}`);
  const manualEntryCheckbox = document.getElementById(`manual-entry-${lineId}`);
  const dropdownFields = document.getElementById(`dropdown-fields-${lineId}`);
  const manualFields = document.getElementById(`manual-fields-${lineId}`);

  // Get the vehicle selector container to access all elements
  const vehicleSelector = document.querySelector(
    `.vehicle-selector[data-line-id="${lineId}"]`
  );
  const manualEntryToggle = vehicleSelector?.querySelector(
    ".vehicle-selector__toggle"
  );
  const updateVehicleButton = vehicleSelector?.querySelector(
    ".vehicle-selector__actions"
  );

  if (ymmWaivedCheckbox.checked) {
    // Show save button
    saveButton.style.display = "inline-block";

    // Hide manual entry toggle section
    if (manualEntryToggle) {
      manualEntryToggle.style.display = "none";
    }

    // Hide update vehicle button
    if (updateVehicleButton) {
      updateVehicleButton.style.display = "none";
    }

    // Disable and hide all other fields
    manualEntryCheckbox.disabled = true;
    dropdownFields.style.display = "none";
    manualFields.style.display = "none";
  } else {
    // Hide save button
    saveButton.style.display = "none";

    // Show manual entry toggle section
    if (manualEntryToggle) {
      manualEntryToggle.style.display = "block";
    }

    // Show update vehicle button
    if (updateVehicleButton) {
      updateVehicleButton.style.display = "block";
    }

    // Re-enable manual entry checkbox
    manualEntryCheckbox.disabled = false;

    // Show appropriate fields based on manual entry checkbox state
    if (manualEntryCheckbox.checked) {
      dropdownFields.style.display = "none";
      manualFields.style.display = "grid";
    } else {
      dropdownFields.style.display = "grid";
      manualFields.style.display = "none";
    }
  }
}

// Add this function to handle the YMM waiver save button
async function rapido_saveYmmWaiver(lineId) {
  console.log("Saving YMM waiver for line:", lineId);

  try {
    // Step 1: Fetch the current cart to get the real quantity
    const cartResponse = await fetch("/cart.js");
    const cart = await cartResponse.json();

    // Step 2: Find the line item by key (lineId)
    const lineItem = cart.items.find((item) => item.key === lineId);

    if (!lineItem) {
      console.error("Line item not found in cart:", lineId);
      alert(
        "Error: Could not find item in cart. Please refresh and try again."
      );
      return;
    }

    const currentQuantity = lineItem.quantity;
    console.log("Current quantity for line", lineId, ":", currentQuantity);

    // Step 3: Update the line item with ymm_waived property and preserve quantity
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: lineId.toString(), // Convert to string as required by Shopify cart API
        quantity: currentQuantity, // Preserve the current quantity from cart
        properties: {
          ymm_waived: true,
        },
      }),
    });

    if (response.ok) {
      console.log("YMM waiver saved successfully");
      // Refresh cart display
      window.location.reload();
    } else {
      const errorText = await response.text();
      console.error("Error saving YMM waiver:", errorText);
      alert("Failed to save YMM waiver. Please try again.");
    }
  } catch (error) {
    console.error("Error in rapido_saveYmmWaiver:", error);
    alert("An error occurred. Please try again.");
  }
}

// Display order confirmation popup
function rapido_showOrderConfirmation(orderData) {
  console.log("Showing order confirmation popup", orderData);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NZ", {
      style: "currency",
      currency: orderData.currency || "NZD",
    }).format(amount);
  };

  // Build line items HTML
  let lineItemsHTML = "";
  orderData.line_items.forEach((item) => {
    lineItemsHTML += `
      <tr>
        <td class="order-item__name">
          <strong>${item.title}</strong>
          ${
            item.variant_title ? `<br><small>${item.variant_title}</small>` : ""
          }
          ${item.sku ? `<br><small>SKU: ${item.sku}</small>` : ""}
        </td>
        <td class="order-item__quantity text-center">${item.quantity}</td>
        <td class="order-item__price text-right">${formatCurrency(
          item.price
        )}</td>
        <td class="order-item__total text-right">${formatCurrency(
          item.price * item.quantity
        )}</td>
      </tr>
    `;
  });

  // Build the popup content
  const content = `
    <div class="order-confirmation-popup">
      <header class="section-header text-center homepage_subtitle_style_primary">
        <h2>Order Confirmed!</h2>
        <p class="order-number">Order ${orderData.name}</p>
      </header>
      
      <div class="order-details">
        
        ${
          orderData.shipping_address
            ? `
          <div class="order-shipping-info">
            <h3>Delivery Information</h3>
            <div class="address-block">
              <strong>${orderData.shipping_address.company || ""}</strong><br>
              ${orderData.shipping_address.address1}<br>
              ${
                orderData.shipping_address.address2
                  ? orderData.shipping_address.address2 + "<br>"
                  : ""
              }
              ${orderData.shipping_address.city}, ${
                orderData.shipping_address.province
              } ${orderData.shipping_address.zip}<br>
              ${orderData.shipping_address.country}
            </div>
          </div>
        `
            : ""
        }
        
        <div class="order-items-section">
          <h3>Order Items</h3>
          <table class="order-items-table">
            <thead>
              <tr>
                <th>Product</th>
                <th class="text-center">Qty</th>
                <th class="text-right">Price</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${lineItemsHTML}
            </tbody>
          </table>
        </div>
        
        <div class="order-summary">
          <div class="summary-row">
            <span>Subtotal:</span>
            <span>${formatCurrency(orderData.subtotal_price)}</span>
          </div>
          ${
            orderData.shipping_lines && orderData.shipping_lines.length > 0
              ? `
            <div class="summary-row">
              <span>${orderData.shipping_lines[0].title}:</span>
              <span>${formatCurrency(orderData.shipping_lines[0].price)}</span>
            </div>
          `
              : ""
          }
          ${
            orderData.total_tax && parseFloat(orderData.total_tax) > 0
              ? `
            <div class="summary-row">
              <span>GST (15%):</span>
              <span>${formatCurrency(orderData.total_tax)}</span>
            </div>
          `
              : ""
          }
          ${
            orderData.total_discounts &&
            parseFloat(orderData.total_discounts) > 0
              ? `
            <div class="summary-row discount">
              <span>Discount:</span>
              <span>-${formatCurrency(orderData.total_discounts)}</span>
            </div>
          `
              : ""
          }
          <div class="summary-row total">
            <strong>Total:</strong>
            <strong>${formatCurrency(orderData.total_price)}</strong>
          </div>
        </div>
        
        ${
          orderData.note
            ? `
          <div class="order-notes">
            <h3>Order Notes</h3>
            <p>${orderData.note}</p>
          </div>
        `
            : ""
        }
      </div>
      
      <div class="order-actions">
        <a href="/" class="btn btn--secondary">Continue Shopping</a>
        <button onclick="rapido_closeOrderConfirmation()" class="btn btn--tertiary">Close</button>
      </div>
    </div>
    
    <style>
      .order-confirmation-popup {
        max-width: 800px;
        margin: 0 auto;
        height: 90vh;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .modal__dialog {
          overflow: hidden !important;
      }
      
      .order-number {
        font-size: 18px;
        color: #666;
        margin-top: 10px;
      }
      
      
      .order-details {
        flex: 1;
        overflow-y: auto;
        padding: 30px;
        margin-top: 0;
      }
      
      .order-customer-info,
      .order-shipping-info,
      .order-items-section,
      .order-notes {
        margin-bottom: 30px;
        padding: 20px;
        background: #f8f8f8;
        border-radius: 8px;
      }
      
      .order-details h3 {
        margin-bottom: 15px;
        font-size: 18px;
        color: #333;
      }
      
      .info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 10px;
      }
      
      .info-item {
        margin-bottom: 5px;
      }
      
      .address-block {
        line-height: 1.6;
      }
      
      .order-items-table {
        width: 100%;
        border-collapse: collapse;
      }
      
      .order-items-table th,
      .order-items-table td {
        padding: 12px;
        text-align: left;
        border-bottom: 1px solid #ddd;
      }
      
      .order-items-table th {
        background: #f0f0f0;
        font-weight: 600;
      }
      
      .order-summary {
        background: #f8f8f8;
        padding: 20px;
        border-radius: 8px;
        margin-bottom: 20px;
      }
      
      .summary-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
        padding: 5px 0;
      }
      
      .summary-row.total {
        border-top: 2px solid #333;
        padding-top: 15px;
        margin-top: 15px;
        font-size: 18px;
      }
      
      .summary-row.discount {
        color: #d32f2f;
      }
      
      .order-actions {
        display: flex;
        gap: 15px;
        justify-content: center;
        margin-top: 0;
        flex-wrap: wrap;
        flex-shrink: 0;
        padding: 20px 30px 30px 30px;
        background: white;
        border-top: 1px solid #eee;
      }
      
      .order-actions .btn {
        padding: 12px 30px;
      }
      
      .order-actions .btn--tertiary {
        background: transparent;
        border: 1px solid #ccc;
        color: #666;
      }
      
      @media (max-width: 768px) {
        .order-confirmation-popup {
          height: 100vh;
          max-height: 100vh;
        }
        
        
        .order-details {
          padding: 20px;
        }
        
        .order-items-table {
          font-size: 14px;
        }
        
        .order-items-table th,
        .order-items-table td {
          padding: 8px;
        }
        
        .order-actions {
          padding: 15px 20px 20px 20px;
          flex-direction: column;
        }
        
        .order-actions .btn {
          width: 100%;
        }
      }
    </style>
  `;

  // Show the modal
  const modal = document.querySelector("#modal-quick-view");
  if (modal) {
    const modalInner = modal.querySelector(".modal__inner");
    modalInner.innerHTML = content;
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("overflow-hidden");
  } else {
    console.error("Modal element not found");
  }
}


// Close order confirmation popup
function rapido_closeOrderConfirmation() {
  const modal = document.querySelector("#modal-quick-view");
  if (modal) {
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("overflow-hidden");

    // Clear the modal content after animation
    setTimeout(() => {
      const modalInner = modal.querySelector(".modal__inner");
      if (modalInner) {
        modalInner.innerHTML = "";
      }
    }, 200);
    // Reload the page
    window.location.reload();
  }
}

// Add event listener for closing order confirmation modal with close-quick-view button
document.addEventListener("click", function(e) {
  const target = e.target;
  
  // Check if clicked element is or is inside a close-quick-view button
  if (target.classList.contains("close-quick-view") || target.closest(".close-quick-view")) {
    // Check if we're inside a modal that contains an order confirmation
    const modal = target.closest("#modal-quick-view");
    if (modal && modal.querySelector(".order-confirmation-popup")) {
      e.preventDefault();
      e.stopPropagation(); // Prevent other handlers from firing
      rapido_closeOrderConfirmation();
    }
  }
});
